import { parseIsoMs } from "@/features/opportunities/lib/format-relative-time";
import type { EventActionItem, EventJobItem } from "@/features/events/api/events.api";

export interface ActionWaitDisplay {
  /** How long the user has waited: the final total, or the wait so far. */
  elapsedMs: number;
  /** True while some event of the action is still owed an outcome. */
  isUnfinished: boolean;
  /** Unfinished and already past its budget — the reading worth chasing. */
  isOverdue: boolean;
}

/**
 * What an action's Waited cell should say. An unfinished action reports the
 * wait so far rather than a dash: standing at "—" while somebody is still
 * waiting is the reading that made the tab look healthy during an outage.
 */
export function getActionWaitDisplay(
  action: Pick<EventActionItem, "endToEndMs" | "startedAt">,
  now: number,
  slowAfterMs: number,
): ActionWaitDisplay {
  if (action.endToEndMs != null) {
    return { elapsedMs: action.endToEndMs, isUnfinished: false, isOverdue: false };
  }

  const startedAt = parseIsoMs(action.startedAt);
  if (startedAt == null) {
    return { elapsedMs: 0, isUnfinished: true, isOverdue: false };
  }

  const elapsedMs = Math.max(0, now - startedAt);
  return { elapsedMs, isUnfinished: true, isOverdue: elapsedMs > slowAfterMs };
}

export function hasUnfinishedEventActions(
  rows: Array<Pick<EventActionItem, "endToEndMs">>,
): boolean {
  return rows.some((row) => row.endToEndMs === null);
}

/**
 * Which event in an action's fan-out the drawer should open on: the operator's
 * pick if it is still in the list, otherwise the first failure, otherwise the
 * first event in publish order.
 */
export function selectActionFlowEvent(
  events: EventJobItem[],
  selectedId: string | null,
): EventJobItem | null {
  if (selectedId) {
    const selected = events.find((event) => event.id === selectedId);
    if (selected) return selected;
  }
  return events.find((event) => event.status === "FAILED") ?? events[0] ?? null;
}
